import React, { useState } from 'react';
import type { CoachSuggestion } from '../types';
import {
  BrainCircuit,
  AlertTriangle,
  AlertOctagon,
  Info,
  X,
  ChevronDown,
  ChevronUp,
  Lightbulb,
} from 'lucide-react';

interface Props {
  suggestions: CoachSuggestion[];
}

const TYPE_LABELS: Record<string, string> = {
  missing_pattern: 'Brakujący wzorzec',
  muscle_imbalance: 'Dysbalans mięśniowy',
  exercise_redundancy: 'Nadmiar ćwiczeń',
  fatigue_risk: 'Ryzyko zmęczenia',
  progression: 'Progresja',
  general: 'Ogólne',
};

const TYPE_COLORS: Record<string, string> = {
  missing_pattern: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
  muscle_imbalance: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
  exercise_redundancy: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300',
  fatigue_risk: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
  progression: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
  general: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
};

export default function CoachPanel({ suggestions }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const visible = suggestions.filter((s) => !dismissed.has(s.id));
  const criticalCount = visible.filter((s) => s.severity === 'critical').length;
  const warningCount = visible.filter((s) => s.severity === 'warning').length;
  const infoCount = visible.filter((s) => s.severity === 'info').length;

  const dismiss = (id: string) => {
    setDismissed((prev) => new Set(prev).add(id));
  };

  const severityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon size={14} className="text-red-500 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />;
      default:
        return <Info size={14} className="text-blue-400 flex-shrink-0" />;
    }
  };

  const severityBorder = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'border-l-red-500';
      case 'warning':
        return 'border-l-amber-500';
      default:
        return 'border-l-blue-400';
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* ── Header ──────────────────────── */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-950 flex items-center justify-center">
          <BrainCircuit size={22} className="text-brand-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold">Trener AI</h2>
          <p className="text-xs text-slate-400">
            Analiza Twojego programu oparta na regułach
          </p>
        </div>
      </div>

      {/* ── Summary badges ──────────────── */}
      <div className="flex items-center gap-3 mb-4">
        {criticalCount > 0 && (
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 text-xs font-bold">
            <AlertOctagon size={12} />
            {criticalCount} Krytycznych
          </span>
        )}
        {warningCount > 0 && (
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 text-xs font-bold">
            <AlertTriangle size={12} />
            {warningCount} Ostrzeżeń
          </span>
        )}
        {infoCount > 0 && (
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-bold">
            <Info size={12} />
            {infoCount} Info
          </span>
        )}
        {visible.length === 0 && (
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 text-xs font-bold">
            ✓ Brak wykrytych problemów
          </span>
        )}
      </div>

      {/* ── Suggestions ─────────────────── */}
      {visible.length === 0 ? (
        <div className="text-center py-12">
          <Lightbulb size={32} className="text-green-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Twój program wygląda świetnie!</p>
          <p className="text-xs text-slate-400 mt-1">
            Brak krytycznych problemów lub sugestii ulepszeń.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((s) => {
            const isExpanded = expandedId === s.id;
            return (
              <div
                key={s.id}
                className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 border-l-4 ${severityBorder(
                  s.severity
                )} rounded-lg overflow-hidden transition-all`}
              >
                <div
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : s.id)}
                >
                  {severityIcon(s.severity)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className="text-sm font-semibold truncate">{s.title}</h4>
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                          TYPE_COLORS[s.type] || TYPE_COLORS.general
                        }`}
                      >
                        {TYPE_LABELS[s.type] || s.type}
                      </span>
                    </div>
                    {!isExpanded && (
                      <p className="text-xs text-slate-500 truncate">{s.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={14} className="text-slate-400" />
                    ) : (
                      <ChevronDown size={14} className="text-slate-400" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dismiss(s.id);
                      }}
                      className="p-0.5 text-slate-300 hover:text-slate-500 transition"
                      title="Odrzuć"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-3 animate-slide-down">
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      {s.description}
                    </p>
                    {s.actionable && (
                      <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold text-brand-600 dark:text-brand-400">
                        <Lightbulb size={10} />
                        Wykonalne - dostosuj program, aby rozwiązać
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
